import React, { useState, useMemo, useEffect } from "react";
import {
  Layout,
  Steps,
  Card,
  Form,
  Select,
  Button,
  Upload,
  Result,
  Typography,
  Space,
  Tag,
  Divider,
  Alert,
  Table,
  message,
} from "antd";
import {
  InboxOutlined,
  DownloadOutlined,
  CheckCircleFilled,
  ReloadOutlined,
  CloudUploadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { getToken } from "../../helpers";
import { API, BEARER } from "../../constant";
import { downloadUrl } from "../../constant";

const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

// ---- Mock async calls (swap these for real API calls) --------------------
function mockDownloadTemplate(values) {
  return new Promise((resolve) => {
    setTimeout(() => {
      const rows = [
        ["sku_id", "product_name", "quantity", "unit_price"],
        ["SKU-1001", "Sample Product A", "100", "25.50"],
        ["SKU-1002", "Sample Product B", "50", "40.00"],
      ];
      const csv = rows.map((r) => r.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bulk_upload_template_${values.launch}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      resolve(true);
    }, 700);
  });
}

function mockUploadFile(file) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      const isGoodFile = !file.name.toLowerCase().includes("bad");
      if (isGoodFile) {
        resolve({ successCount: 48, totalCount: 50 });
      } else {
        reject({
          message: "3 rows failed validation",
          errors: [
            {
              row: 4,
              field: "quantity",
              issue: "Value must be a positive number",
            },
            { row: 11, field: "sku_id", issue: "SKU not found in catalog" },
            { row: 27, field: "unit_price", issue: "Missing required value" },
          ],
        });
      }
    }, 1200);
  });
}

// ------------------------------------------------------------------

const BulkUploadPage = () => {
  const [form] = Form.useForm();
  const [filtersComplete, setFiltersComplete] = useState(false);
  const [templateDownloaded, setTemplateDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { status: 'success' | 'error', data }
  const [isLoading1, setIsLoading] = useState(true);
  const [fileList, setFileList] = useState([]);
  const [launchMasterListArray, setLaunchMasterListArray] = useState([]);
  const [selectedLaunch, setSelectedLaunch] = useState([]);
  const authToken = getToken();

  const currentStep = useMemo(() => {
    if (uploadResult) return 3;
    if (templateDownloaded) return 2;
    if (filtersComplete) return 1;
    return 0;
  }, [filtersComplete, templateDownloaded, uploadResult]);

  const handleValuesChange = (_, allValues) => {
    const complete = ["launch"].every((key) => !!allValues[key]);

    // only launch
    setFiltersComplete(complete);
    // Changing filters after a template was pulled invalidates the old one
    if (templateDownloaded) {
      setTemplateDownloaded(false);
      setFileList([]);
      setUploadResult(null);
    }
  };

  const fetchLaunchList = async (token) => {
    let optionsData2 = [];
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API}/launch-masters?&populate[season][fields][0]=season_id&createdAt:desc`,
        {
          headers: { Authorization: `${BEARER} ${token}` },
        },
      );
      const optionsData = await response.json();

      optionsData2 = optionsData?.data?.map((entry) => {
        return {
          id: entry?.id,
          launchName: entry?.attributes?.launchName,
          launchDate: entry?.attributes?.launchDate,
          launchDrop: entry?.attributes?.launchDrop,
          //   launchCat: entry?.attributes?.launchCat,

          seasonName: entry?.attributes?.season?.data?.attributes?.season_id,
          seasonRefId: entry?.attributes?.season?.data?.id,
          // launch_masters: entry?.attributes?.launch_masters,
        };
      });

      setLaunchMasterListArray(optionsData2);
      console.log("original option Data:", optionsData);
      console.log("modified Data:", optionsData2);
    } catch (error) {
      console.error(error);
      message.error("Error while fetching Data!");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (token) => {
    setDownloading(true);
    try {
      const getBulkUploadTemplate = await fetch(
        `${API}/core-product/core-product-bulk-update-template`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `${BEARER} ${token}`,
          },
          body: JSON.stringify({ launchId: selectedLaunch }),
        },
      );
      const resultData = await getBulkUploadTemplate?.json();
      console.log(
        "bulk template error: ",
        resultData,
        resultData?.data[0]?.url,
      );
      const downloadfunction = (url) => {
        window.open(`${downloadUrl}${url}`, "_blank", "noreferrer");
      };
      downloadfunction(resultData?.data[0]?.url);

      setTemplateDownloaded(true);
      message.success("Template downloaded. Fill it in, then upload it below.");
    } catch (e) {
      message.error("Couldn't generate the template. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append("files", file);

      const response = await fetch(`${API}/core-product/bulk-upload-template`, {
        method: "POST",
        headers: {
          Authorization: `${BEARER} ${authToken}`,
        },
        body: formData,
      });
      const data = await response.json();
      console.log("upload response data:", data);

      if (!response.ok) {
        throw new Error(data?.message || "File upload failed");
      }

      setUploadResult({ status: "success", data });
      onSuccess("ok");
    } catch (err) {
      setUploadResult({ status: "error", data: err });
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  const startOver = () => {
    form.resetFields();
    setFiltersComplete(false);
    setTemplateDownloaded(false);
    setUploadResult(null);
    setFileList([]);
  };

  const errorColumns = [
    { title: "Row", dataIndex: "row", key: "row", width: 80 },
    { title: "Field", dataIndex: "field", key: "field", width: 140 },
    { title: "Issue", dataIndex: "issue", key: "issue" },
  ];

  useEffect(() => {
    console.log("inside useEffect funciton ");
    fetchLaunchList(getToken());
  }, []);

  // ---- Mock reference data -------------------------------------------------

  const launchListOptions = launchMasterListArray.map((launch) => {
    return {
      label: `${launch.launchName} - ${launch.seasonName} `,
      value: launch.id,
    };
  });

  // comment extra
  const FILTER1_OPTIONS = [
    { value: "region_north", label: "North Region" },
    { value: "region_south", label: "South Region" },
    { value: "region_west", label: "West Region" },
  ];
  const FILTER2_OPTIONS = [
    { value: "category_a", label: "Category A" },
    { value: "category_b", label: "Category B" },
  ];
  const FILTER3_OPTIONS = [
    { value: "tier_1", label: "Tier 1" },
    { value: "tier_2", label: "Tier 2" },
    { value: "tier_3", label: "Tier 3" },
  ];
  const onChange = (value) => {
    console.log(`selected ${value}`);
  };

  const onSearch = (value) => {};

  return (
    <div style={{ width: "100%", maxWidth: 860 }}>
      <Paragraph style={{ color: "#5B6472", marginBottom: 28 }}>
        Choose a launch, download the matching template, fill it in
        offline, then upload it here.
      </Paragraph>

      <Steps
        current={currentStep}
        items={[
          { title: "Select filters" },
          { title: "Download template" },
          { title: "Upload file" },
          { title: "Result" },
        ]}
        style={{ marginBottom: 28 }}
      />

      {/* Step 1 & 2: Filters + Download */}
      <Card
        title="1. Select filters"
        style={{ marginBottom: 20, borderRadius: 10 }}
        extra={
          filtersComplete && <Tag color="success">All filters selected</Tag>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleValuesChange}
          disabled={uploading}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 16,
            }}
          >
            <Form.Item
              name="launch"
              label="Launch"
              rules={[{ required: true, message: "Required" }]}
            >
              <Select
                showSearch
                onSearch={(value) => {}}
                onChange={(value) => {
                  console.log("value of launch selected", value);
                  setSelectedLaunch(value);
                }}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.trim().toLowerCase())
                }
                filterSort={(optionA, optionB) =>
                  (optionA?.label ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.label ?? "").toLowerCase())
                }
                placeholder="Select launch"
                options={launchListOptions}
                allowClear
              />
            </Form.Item>
          </div>
        </Form>

        <Divider style={{ margin: "8px 0 20px" }} />

        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="large"
            disabled={!filtersComplete}
            loading={downloading}
            onClick={() => handleDownload(authToken)}
          >
            Download bulk upload template
          </Button>
          {!filtersComplete && (
            <Text type="secondary">
              Select all four filters to enable the download.
            </Text>
          )}
          {templateDownloaded && (
            <Text type="success">
              <CheckCircleFilled /> Template downloaded for the selected
              filters.
            </Text>
          )}
        </Space>
      </Card>

      {/* Step 3: Upload */}
      <Card
        title="2. Upload completed template"
        style={{
          marginBottom: 20,
          borderRadius: 10,
          opacity: templateDownloaded ? 1 : 0.5,
        }}
      >
        {!templateDownloaded ? (
          <Alert
            type="info"
            showIcon
            message="Download the template above first"
            description="The upload area unlocks once a template has been generated for your selected filters."
          />
        ) : (
          <Dragger
            fileList={fileList}
            onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
            customRequest={handleUpload}
            accept=".xlsx,.xls,.csv"
            maxCount={1}
            disabled={uploading}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined style={{ color: "#3FB68B" }} />
            </p>
            <p className="ant-upload-text">
              Click or drag the filled template here
            </p>
            <p className="ant-upload-hint">
              Supports .xlsx, .xls and .csv — one file at a time
            </p>
          </Dragger>
        )}
      </Card>

      {/* Step 4: Result */}
      {uploadResult && uploadResult.status === "success" && (
        <Card style={{ borderRadius: 10 }}>
          <Result
            status="success"
            icon={<CheckCircleFilled style={{ color: "#3FB68B" }} />}
            title="Upload successful"
            subTitle={`${uploadResult.data.successCount} of ${uploadResult.data.totalCount} rows were processed successfully.`}
            extra={[
              <Button key="again" icon={<ReloadOutlined />} onClick={startOver}>
                Start a new upload
              </Button>,
            ]}
          />
        </Card>
      )}

      {uploadResult && uploadResult.status === "error" && (
        <Card style={{ borderRadius: 10 }}>
          <Result
            status="error"
            title="Upload failed"
            subTitle={uploadResult.data.message}
          />
          <Table
            size="small"
            columns={errorColumns}
            dataSource={uploadResult.data.errors.map((e, i) => ({
              ...e,
              key: i,
            }))}
            pagination={false}
            style={{ marginBottom: 20 }}
          />
          <Space>
            <Button
              type="primary"
              icon={<CloudUploadOutlined />}
              onClick={() => {
                setUploadResult(null);
                setFileList([]);
              }}
            >
              Try uploading again
            </Button>
            <Button onClick={startOver}>Start over</Button>
          </Space>
        </Card>
      )}
    </div>
  );
};
// Wrap with AntApp so message/notification hooks have context when this
// file is rendered standalone.
export default BulkUploadPage;
